import React, { useCallback, useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { Image, Separator, YStack, StackProps } from 'tamagui'

import { useParams } from 'react-router-dom'

import { SPONSORS, SponsorType } from '../data/config/sponsors'
import { getSlugFromPrettyUrl } from '../data/util/urlHelpers'

import { Card } from './Card'
import { Paragraph } from './Text'
import { ExternalLink, useCreateLinkPressHandler } from './Link'

import { SponsorsSection_query$key } from './__generated__/SponsorsSection_query.graphql'

export const SponsorsSection = ({
  query,
  ...props
}: {
  query: SponsorsSection_query$key
} & StackProps) => {
  const data = useFragment(
    graphql`
      fragment SponsorsSection_query on Query {
        viewer {
          emails {
            email
            verified
          }
        }
      }
    `,
    query
  )

  const params = useParams()

  const sponsorsToDisplay = useMemo(() => {
    return SPONSORS.sort(() => (Math.random() > 0.5 ? -1 : 1))
      .filter((sponsor) => {
        const previewDomains = sponsor.preview

        if (previewDomains === undefined) {
          return true
        }

        if (!data.viewer || !Array.isArray(data.viewer.emails)) {
          return false
        }

        return data.viewer.emails.some((email) => {
          if (!email?.verified || !email.email) {
            return false
          }

          const lowerCaseEmail = email.email.toLowerCase()

          if (lowerCaseEmail.endsWith('guild.host')) {
            return true
          }

          return previewDomains.some((domain) =>
            lowerCaseEmail.endsWith(domain)
          )
        })
      })
      .filter((sponsor) => {
        if (sponsor.eventSlugScope) {
          if (params.eventId) {
            const eventSlug = getSlugFromPrettyUrl(params.eventId)

            return sponsor.eventSlugScope.some((slug) => slug === eventSlug)
          }

          return false
        }

        return true
      })
      .slice(0, 2)
  }, [params])

  if (!sponsorsToDisplay.length) {
    return null
  }

  return (
    <Card {...props} gap='$4'>
      <Paragraph size='$4' fontWeight='$5' color='$grey7'>
        {sponsorsToDisplay.length === 1
          ? 'Platform Sponsor'
          : 'Platform Sponsors'}
      </Paragraph>

      <YStack gap='$4'>
        {sponsorsToDisplay.map((sponsor, index) => {
          return (
            <React.Fragment key={sponsor.name}>
              {index !== 0 && <Separator borderColor='$grey5' />}

              <Sponsor sponsor={sponsor} />
            </React.Fragment>
          )
        })}
      </YStack>
    </Card>
  )
}

const Sponsor = ({ sponsor }: { sponsor: SponsorType }) => {
  const linkAction = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.open(sponsor.webURL, '_blank')
    }
  }, [sponsor.webURL])

  const { linkPressHandler, anchorRef, clickableRef } =
    useCreateLinkPressHandler(linkAction)

  return (
    <YStack
      gap='$4'
      cursor='pointer'
      ref={anchorRef}
      onPress={linkPressHandler}
      marginTop={sponsor.marginTop}
    >
      <img src={sponsor.lightLogoURL} style={{ maxWidth: 150 }} />
      {/* <Image
        source={{
          // width: 150,
          // height: 87 / 2,

          uri: sponsor.lightLogoURL,
        }}
        width={150}
        // width='100%'
        // height='100%'
        // height={87 / 2}
        // resizeMode='contain'
      /> */}

      <Paragraph size='$4'>{sponsor.description}</Paragraph>

      <ExternalLink href={sponsor.webURL} size='$4'>
        {sponsor.linkText}
      </ExternalLink>
    </YStack>
  )
}
